import {
  Text,
  GreyGrey60,
  MagicTable,
  MagicTableCell,
  useMagicTableInfiniteQuery,
  MagicTableFilterConfig,
  useLinkMagicTableWithSearchParams,
  MagicTableEmptyState,
  MagicTableFilterEmptyState,
  IconUserDuo,
  Flex
} from '@sequencehq/core-components'
import { dateTimeWithFormat } from '@sequencehq/utils'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import {
  SeatBalanceFilters,
  SeatBalancesView
} from 'modules/Seats/SeatBalances/types'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import Spinner from 'components/Loading'
import { useNavigate } from 'react-router-dom'
import { customerIdFilterConfig } from 'Customer/index'

export const SeatBalancesMagicTable = () => {
  const navigate = useNavigate()

  const filters: MagicTableFilterConfig<SeatBalanceFilters>[] = [
    {
      ...customerIdFilterConfig,
      paramName: 'customerIds'
    }
  ]

  const { activeFilters, onChangeActiveFilters, sortBy, onChangeSortBy } =
    useLinkMagicTableWithSearchParams(filters)

  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboard20240730Client.getSeatBalances,
    filters,
    activeFilters,
    sortBy
  )

  return (
    <CurrentUserId>
      {userId => (
        <MagicTable<SeatBalancesView>
          activeFilters={activeFilters}
          onChangeActiveFilters={onChangeActiveFilters}
          sortBy={sortBy}
          onChangeSortBy={onChangeSortBy}
          entityNamePlural="seat balances"
          sequenceUserId={userId}
          infiniteQuery={infiniteQuery}
          getSubRows={row => {
            return (
              row?.balances?.map(balance => {
                return {
                  ...balance,
                  id: '',
                  sequenceAccountId: '',
                  legalName: '',
                  customerAliases: [],
                  balances: []
                }
              }) ?? []
            )
          }}
          columns={[
            {
              id: 'customerAliases',
              accessorKey: 'customerAliases',
              header: 'Customer',
              cell: value => {
                // Only show customer card for parent rows
                const isChildRow = value.row.depth > 0
                if (isChildRow) {
                  return null
                }

                // Always render as a pill, regardless of alias presence
                const customerId = value.row.original.id
                return (
                  <Flex alignItems="center" height="40px" pl="6px">
                    <CustomerPreviewCardPill
                      customerId={customerId}
                      variant="ghost"
                    />
                  </Flex>
                )
              }
            },
            {
              id: 'seatType',
              accessorKey: 'seatType',
              header: 'Seat metric',
              cell: value => {
                const isSubRow = value.row.depth > 0
                const totalSubRows = value.row.subRows.length
                const hasSubRows = totalSubRows > 0

                if (!hasSubRows && !isSubRow) {
                  return (
                    <MagicTableCell
                      text={value.getValue<
                        SeatBalancesView['model']['balances'][number]['seatType']
                      >()}
                    />
                  )
                }

                if (hasSubRows && !isSubRow) {
                  return (
                    <MagicTableCell
                      text={`${totalSubRows} seat ${
                        totalSubRows > 1 ? 'metrics' : 'metric'
                      }`}
                      textColor={GreyGrey60}
                    />
                  )
                }
                return (
                  <MagicTableCell
                    text={value.getValue<
                      SeatBalancesView['model']['balances'][number]['seatType']
                    >()}
                  />
                )
              }
            },
            {
              id: 'total',
              accessorKey: 'total',
              header: 'Balance',
              cell: value => (
                <MagicTableCell
                  text={String(
                    value.getValue<SeatBalancesView['model']['total']>()
                  )}
                />
              )
            },
            {
              id: 'changeTimestamp',
              accessorKey: 'changeTimestamp',
              header: 'Last Updated (UTC)',
              cell: value => {
                const changeTimestamp =
                  value.getValue<SeatBalancesView['model']['changeTimestamp']>()

                return (
                  <MagicTableCell
                    text={dateTimeWithFormat(
                      changeTimestamp,
                      'dd-MM-yyyy ∙ HH:mm:ss'
                    )}
                  />
                )
              }
            }
          ]}
          emptyStateUnfiltered={entityNamePlural => (
            <MagicTableEmptyState
              data-testid="seatBalances.emptyState"
              entityNamePlural={entityNamePlural}
              imageSrc="/img/empty/gauge.png"
              learnMoreText="Seat balances reflect the latest number of seats in use for a customer"
              learnMoreHref="https://docs.sequencehq.com/seats/seat-balances"
            />
          )}
          emptyStateFiltered={(entityNamePlural, clearFilters) => (
            <MagicTableFilterEmptyState
              entityNamePlural={entityNamePlural}
              entityIcon={IconUserDuo}
              actionClearFilters={clearFilters}
              actionViewAll={() => navigate('/seat-balances')}
              variant="TABLE"
            />
          )}
          filters={filters}
        />
      )}
    </CurrentUserId>
  )
}
